import React from 'react';
import PropTypes from 'prop-types';
import lodashPadStart from 'lodash/padStart';

import { formatPrice } from 'common/formatters';
import { STRIPE_ACCOUNT_TYPE_CARD } from 'common/stripeConstants';

import SrBox from 'client/components/SrBox';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrSeparator from 'client/components/SrSeparator';
import SrText from 'client/components/SrText';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import { isHomebuyerPartner } from 'common/utility/helpers';

const UPPartnerPayoutFlowConnectBankStep = ({
  utilityPartner,
  utilityPartnerStripeAccount,
  currentPendingBalance,
  onContinueTransfer,
  withdrawErrorMessage,
  isLoading,
  onClose,
}) => {
  const transferText = utilityPartnerStripeAccount
    ? utilityPartnerStripeAccount?.type === STRIPE_ACCOUNT_TYPE_CARD
      ? 'Transfer to card: '
      : 'Transfer to bank account: '
    : '';

  const accountText = utilityPartnerStripeAccount
    ? utilityPartnerStripeAccount?.type === STRIPE_ACCOUNT_TYPE_CARD
      ? lodashPadStart(utilityPartnerStripeAccount.cardLast4, 8, '*')
      : lodashPadStart(utilityPartnerStripeAccount.bankAccountLast4, 8, '*')
    : '';

  const cantWithdraw =
    isLoading || (currentPendingBalance === 0 && utilityPartnerStripeAccount?.id);

  return (
    <SrDarkModal onClose={onClose}>
      <SrModalContent style={{ width: 412, marginTop: 4 }}>
        <SrBox display="flex" alignItems="center" justifyContent="center" flexDirection="column">
          <SrText
            weight="800"
            font="greycliff"
            fontSize="26px"
            lineHeight="36px"
            letterSpacing="0.5"
          >
            Transfer Money
          </SrText>
        </SrBox>
        <SrSeparator marginTop="20px" marginBottom="8px" />
        <SrBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          margin="20px 0px"
        >
          <SrText
            display="block"
            font="greycliff"
            fontSize="68px"
            lineHeight="82px"
            weight="800"
            color="green"
          >
            {formatPrice(currentPendingBalance)}
          </SrText>
          <SrText display="block" font="greycliff" fontSize="16px" lineHeight="25px" weight="600">
            {isHomebuyerPartner(utilityPartner)
              ? 'Utility Wallet Balance'
              : 'Utility Profit Balance'}
          </SrText>
        </SrBox>
        {utilityPartnerStripeAccount?.id && (
          <>
            <SrBox margin="20px 0px" textAlign="center">
              <SrText
                display="inlineBlock"
                font="greycliff"
                fontSize="16px"
                lineHeight="25px"
                weight="600"
              >
                {transferText}
              </SrText>
              <SrText
                display="inlineBlock"
                font="greycliff"
                fontSize="16px"
                lineHeight="25px"
                weight="600"
                color="green"
              >
                {accountText}
              </SrText>
              <SrText
                display="block"
                font="greycliff"
                fontSize="16px"
                lineHeight="25px"
                weight="600"
                color="greyMonochrome"
              >
                Takes 5-7 business days.
              </SrText>
            </SrBox>
          </>
        )}
        {withdrawErrorMessage && (
          <SrBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            margin="20px 0px"
          >
            <SrErrorMessage>{withdrawErrorMessage}</SrErrorMessage>
          </SrBox>
        )}
        <SrBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          marginTop="30px"
        >
          <SrButton
            theme={SR_BUTTON_THEME_ADOBE_GREEN}
            onClick={onContinueTransfer}
            disabled={cantWithdraw}
          >
            {isLoading
              ? 'Requesting...'
              : utilityPartner?.utilityStripeAccountId
              ? 'Transfer'
              : 'Connect Bank'}
          </SrButton>
          <SrButton theme={SR_BUTTON_THEME_ADOBE_GREY} margin="12px 0px" onClick={onClose}>
            Cancel
          </SrButton>
        </SrBox>
      </SrModalContent>
    </SrDarkModal>
  );
};

UPPartnerPayoutFlowConnectBankStep.displayName = 'UPPartnerPayoutFlowConnectBankStep';

UPPartnerPayoutFlowConnectBankStep.propTypes = {
  utilityPartner: PropTypes.object.isRequired,
  currentPendingBalance: PropTypes.number.isRequired,
  onContinueTransfer: PropTypes.func.isRequired,
  withdrawErrorMessage: PropTypes.string,
  utilityPartnerStripeAccount: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default UPPartnerPayoutFlowConnectBankStep;
